import { z } from 'zod';

/**
 * Workflow→API artifact upload (UCH-151). Authenticated by the per-release
 * upload token (Authorization: Bearer <token>), injected as a workflow input at
 * Confirm — not by Firebase auth. The token maps to exactly one Running release.
 */
/**
 * Canonical platform slugs follow the Tauri target convention
 * (darwin/windows/linux) — the updater manifest key is `${platform}-${arch}`,
 * so anything else can never match. Aliases are folded on every write path.
 */
const PLATFORM_ALIASES: Record<string, string> = {
  macos: 'darwin',
  osx: 'darwin',
  win: 'windows',
  win32: 'windows',
};
export const normalizePlatform = (platform: string): string =>
  PLATFORM_ALIASES[platform] ?? platform;

const PlatformField = z.string().trim().min(1).max(40).transform(normalizePlatform);

export const ArtifactUploadFieldsSchema = z.object({
  platform: PlatformField,
  arch: z.string().trim().min(1).max(40),
  filename: z.string().trim().min(1).max(200),
  contentType: z.string().trim().min(1).max(100),
  sizeBytes: z.number().int().positive(),
  checksumSha256: z.string().regex(/^[0-9a-f]{64}$/),
  /** Tauri updater .sig content; required for updater bundles, empty otherwise. */
  tauriSignature: z.string().max(10_000).default(''),
});
export type ArtifactUploadFields = z.infer<typeof ArtifactUploadFieldsSchema>;

export const ReleaseArtifactSchema = z.object({
  id: z.string().uuid(),
  platform: z.string(),
  arch: z.string(),
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int(),
  checksumSha256: z.string(),
  tauriSignature: z.string(),
  packageId: z.string(),
  uploadedAt: z.string(),
});
export type ReleaseArtifact = z.infer<typeof ReleaseArtifactSchema>;

export const RequiredArtifactSchema = z.object({
  platform: PlatformField,
  arch: z.string().trim().min(1).max(40),
});
export type RequiredArtifact = z.infer<typeof RequiredArtifactSchema>;
